<script lang="ts">
  // GroupManager — create/rename/delete app groups (front+back+db as
  // one section). Talks to GET/PUT/DELETE /api/groups; the agent
  // persists to groups.json and membership propagates within one
  // poll cycle.

  import { onMount } from "svelte";
  import {
    getGroups,
    putGroup,
    deleteGroup,
    type GroupsMap,
    ApiError,
  } from "../lib/api";

  export let onGroupsChanged: (() => void) | null = null;

  let groups: GroupsMap = {};
  let loadError: string | null = null;
  let loading = true;

  // New-group form state.
  let newName = "";
  let newPatterns = "";
  let formError: string | null = null;
  let saving = false;

  // Rename-in-place state.
  let editingName: string | null = null;
  let editingPatterns = "";

  $: groupNames = Object.keys(groups).sort();

  async function refresh(): Promise<void> {
    try {
      groups = await getGroups();
      loadError = null;
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  async function save(name: string, patternsRaw: string): Promise<void> {
    const patterns = patternsRaw
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (!name || patterns.length === 0) {
      formError = "Nombre y al menos un patrón (ej. sola-*) son obligatorios.";
      return;
    }
    saving = true;
    formError = null;
    try {
      groups = await putGroup(name, patterns);
      newName = "";
      newPatterns = "";
      onGroupsChanged?.();
    } catch (e) {
      formError =
        e instanceof ApiError && e.status === 419
          ? "CSRF expirado — recarga la página."
          : e instanceof Error
            ? e.message
            : String(e);
    } finally {
      saving = false;
    }
  }

  function startEdit(name: string): void {
    editingName = name;
    editingPatterns = (groups[name] ?? []).join(", ");
  }

  async function removeGroup(name: string): Promise<void> {
    try {
      groups = await deleteGroup(name);
      onGroupsChanged?.();
    } catch {
      // best-effort; the list refresh will show reality
    }
  }

  onMount(() => {
    void refresh();
  });
</script>

<section class="group-manager" aria-labelledby="gm-heading">
  <header class="group-manager__head">
    <h3 id="gm-heading" class="group-manager__title">Apps / grupos</h3>
    <button type="button" class="gm-btn gm-btn--ghost" on:click={() => void refresh()}>
      Refresh
    </button>
  </header>

  {#if loading}
    <p class="gm-note">Cargando grupos…</p>
  {:else if loadError}
    <p class="gm-error" role="alert">{loadError}</p>
  {:else}
    {#if groupNames.length === 0}
      <p class="gm-note">
        Sin grupos todavía. Crea uno para agrupar front + back + db como una app.
      </p>
    {:else}
      <ul class="gm-list" data-testid="gm-list">
        {#each groupNames as name (name)}
          {#if editingName === name}
            <li class="gm-item gm-item--editing">
              <span class="gm-name mono">{name}</span>
              <input
                class="gm-input"
                bind:value={editingPatterns}
                aria-label={`Patrones de ${name}`}
              />
              <button
                type="button"
                class="gm-btn"
                disabled={saving}
                on:click={() => {
                  void save(name, editingPatterns);
                  editingName = null;
                }}>Save</button>
              <button
                type="button"
                class="gm-btn gm-btn--ghost"
                on:click={() => (editingName = null)}>Cancel</button>
            </li>
          {:else}
            <li class="gm-item" data-testid="gm-item">
              <span class="gm-name mono">{name}</span>
              <span class="gm-patterns mono">{(groups[name] ?? []).join(", ")}</span>
              <button
                type="button"
                class="gm-btn gm-btn--ghost"
                on:click={() => startEdit(name)}>Edit</button>
              <button
                type="button"
                class="gm-btn gm-btn--danger"
                on:click={() => void removeGroup(name)}>Delete</button>
            </li>
          {/if}
        {/each}
      </ul>
    {/if}

    <form
      class="gm-form"
      on:submit|preventDefault={() => void save(newName, newPatterns)}
    >
      <input
        class="gm-input"
        placeholder="app-name"
        aria-label="Nombre del grupo"
        bind:value={newName}
      />
      <input
        class="gm-input gm-input--wide"
        placeholder="patrones separados por coma: sola-front*, sola-api, sola-db"
        aria-label="Patrones de contenedores"
        bind:value={newPatterns}
      />
      <button type="submit" class="gm-btn gm-btn--primary" disabled={saving}>
        Crear grupo
      </button>
    </form>
    {#if formError}
      <p class="gm-error" role="alert">{formError}</p>
    {/if}
  {/if}
</section>

<style>
  .group-manager {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
  }
  .group-manager__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }
  .group-manager__title {
    margin: 0;
    font: var(--type-h2);
    color: var(--text-strong);
  }
  .gm-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .gm-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
  }
  .gm-name {
    font-weight: 700;
    color: var(--accent-cyan);
    min-width: 120px;
  }
  .gm-patterns {
    color: var(--text-mid);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
  .gm-form {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .gm-input {
    height: 32px;
    padding: 0 var(--space-3);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-strong);
    font: var(--type-body-sm);
  }
  .gm-input--wide {
    flex: 1;
    min-width: 260px;
  }
  .gm-btn {
    height: 30px;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-strong);
    font: var(--type-body-sm);
    font-weight: 500;
    cursor: pointer;
  }
  .gm-btn:hover:not(:disabled) {
    background: var(--surface-2);
  }
  .gm-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .gm-btn--primary {
    background: var(--accent-cyan);
    border-color: transparent;
    color: #fff;
  }
  .gm-btn--danger {
    color: var(--err-red);
  }
  .gm-btn--ghost {
    background: transparent;
    border-color: transparent;
    color: var(--text-mid);
  }
  .gm-note {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-mid);
  }
  .gm-error {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--err-red);
  }
</style>
